/**
 * PlansSection — pricing cards sourced LIVE from the DB catalogue
 * (`listPlans()` → GET /api/v1/plans) with graceful degradation:
 *
 *   1. loading  → skeleton cards (never a spinner-only blank).
 *   2. live     → real plans (active only, sorted, a highlighted "populaire"
 *                 tier, a monthly/annual presentation toggle since the numeric
 *                 monthly price supports it).
 *   3. fallback → a curated static 3-tier teaser (TEASER_PLANS) whenever the
 *                 call fails (a logged-out visitor 401s), returns nothing, or
 *                 the network is down. NEVER a crash, never a raw error.
 *
 * Every CTA routes to authentication: a plan CTA deep-links to the in-app
 * subscription page (`returnTo=/settings/subscription`) so the visitor lands
 * there once signed in.
 */

import { type ReactNode, useEffect, useState } from 'react';

import { listPlans, type Plan } from '@/lib/api/subscription';
import { PLANS_SECTION, TEASER_PLANS, type TeaserPlan } from '@/routes/landing/landing-data';
import { Icon, SectionHeading, useLandingCta } from './shared';

const SUBSCRIPTION_PATH = '/settings/subscription';

type State =
  | { status: 'loading' }
  | { status: 'live'; plans: Plan[] }
  | { status: 'fallback' };

/** French labels for the common limit keys; unknown keys are humanised. */
const LIMIT_LABELS: Record<string, string> = {
  projects: 'Projets',
  ai_credits: 'Crédits IA',
  ai_credits_monthly: 'Crédits IA / mois',
  contacts: 'Contacts',
  social_accounts: 'Comptes sociaux',
  accounts: 'Comptes connectés',
  team_members: 'Membres d’équipe',
  seats: 'Utilisateurs',
  scheduled_posts: 'Publications programmées',
  workspaces: 'Espaces de travail',
  campaigns: 'Campagnes',
  storage_gb: 'Stockage (Go)',
};

function humanizeKey(key: string): string {
  const base = LIMIT_LABELS[key] ?? key.replace(/_/g, ' ');
  return base.charAt(0).toUpperCase() + base.slice(1);
}

function formatLimitValue(v: number): string {
  if (v < 0) return 'illimité';
  return new Intl.NumberFormat('fr-CA').format(v);
}

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: currency || 'CAD',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency || 'CAD'}`;
  }
}

export function PlansSection() {
  const { signin } = useLandingCta();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    let alive = true;
    listPlans()
      .then((plans) => {
        if (!alive) return;
        const active = plans
          .filter((p) => p.is_active)
          .sort((a, b) => a.sort_order - b.sort_order);
        setState(active.length > 0 ? { status: 'live', plans: active } : { status: 'fallback' });
      })
      .catch(() => {
        // Logged-out visitors 401 here (the catalogue requires auth); a network
        // outage lands here too. Either way we show the honest static teaser.
        if (alive) setState({ status: 'fallback' });
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section id="tarifs" className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <SectionHeading eyebrow={PLANS_SECTION.eyebrow} title={PLANS_SECTION.title} subtitle={PLANS_SECTION.subtitle} />

      {/* Monthly / annual toggle — only in the live branch where prices are numeric. */}
      {state.status === 'live' && (
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-surface p-1 shadow-elevation-1" role="group" aria-label="Fréquence de facturation">
            <ToggleBtn active={!annual} onClick={() => setAnnual(false)}>
              {PLANS_SECTION.monthly}
            </ToggleBtn>
            <ToggleBtn active={annual} onClick={() => setAnnual(true)}>
              {PLANS_SECTION.annual}
              <span className="ml-1.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                {PLANS_SECTION.annualHint}
              </span>
            </ToggleBtn>
          </div>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
        {state.status === 'loading' && [0, 1, 2].map((i) => <PlanSkeleton key={i} />)}

        {state.status === 'live' &&
          state.plans.map((plan, i) => (
            <LivePlanCard
              key={plan.code}
              plan={plan}
              annual={annual}
              highlighted={state.plans.length >= 2 ? i === 1 : i === 0}
              onChoose={() => signin(SUBSCRIPTION_PATH)}
            />
          ))}

        {state.status === 'fallback' &&
          TEASER_PLANS.map((plan) => (
            <TeaserPlanCard key={plan.code} plan={plan} onChoose={() => signin(SUBSCRIPTION_PATH)} />
          ))}
      </div>

      {state.status === 'fallback' && (
        <p className="mt-6 text-center text-sm text-muted">{PLANS_SECTION.fallbackNote}</p>
      )}
    </section>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'bg-primary text-primary-foreground shadow-elevation-1' : 'text-muted hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

/** Shared card frame used by both the live and teaser cards. */
function PlanFrame({
  highlighted,
  children,
}: {
  highlighted: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`relative flex h-full flex-col rounded-token-xl border bg-surface p-6 shadow-elevation-1 transition duration-200 ease-out hover:-translate-y-1 hover:shadow-elevation-3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-8 ${
        highlighted ? 'border-primary/60 ring-2 ring-primary/30' : 'border-border'
      }`}
    >
      {highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1 text-xs font-semibold text-white shadow-elevation-2">
          {PLANS_SECTION.popularBadge}
        </span>
      )}
      {children}
    </div>
  );
}

function FeatureLi({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Icon name="check" className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" strokeWidth={2.2} />
      <span className="text-sm leading-relaxed text-foreground">{children}</span>
    </li>
  );
}

function ChooseButton({
  highlighted,
  onChoose,
  label,
}: {
  highlighted: boolean;
  onChoose: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      className={`mt-6 inline-flex w-full items-center justify-center rounded-token px-5 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none ${
        highlighted
          ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-elevation-2 hover:brightness-110'
          : 'border border-border bg-surface text-foreground shadow-elevation-1 hover:bg-surface-2'
      }`}
    >
      {label}
    </button>
  );
}

function LivePlanCard({
  plan,
  annual,
  highlighted,
  onChoose,
}: {
  plan: Plan;
  annual: boolean;
  highlighted: boolean;
  onChoose: () => void;
}) {
  const isFree = plan.price_monthly <= 0;
  // Annual view: 2 months offered → per-month equivalent + a "billed annually" note.
  const perMonth = annual ? (plan.price_monthly * 10) / 12 : plan.price_monthly;
  const annualTotal = plan.price_monthly * 10;

  const limitEntries = Object.entries(plan.limits ?? {}).slice(0, 5);

  return (
    <PlanFrame highlighted={highlighted}>
      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
      {plan.description && <p className="mt-1 text-sm text-muted">{plan.description}</p>}

      <div className="mt-5">
        {isFree ? (
          <p className="text-4xl font-extrabold tracking-tight text-foreground">Gratuit</p>
        ) : (
          <>
            <p className="flex items-baseline gap-1">
              <span className="text-4xl font-extrabold tracking-tight text-foreground">
                {formatPrice(perMonth, plan.currency)}
              </span>
              <span className="text-sm font-medium text-muted">/ mois</span>
            </p>
            <p className="mt-1 text-xs text-muted">
              {annual
                ? `Facturé annuellement — ${formatPrice(annualTotal, plan.currency)} / an`
                : 'Facturé mensuellement'}
            </p>
          </>
        )}
      </div>

      <ul className="mt-6 space-y-3">
        {plan.ai_credits_monthly > 0 && (
          <FeatureLi>
            <strong className="font-semibold">
              {new Intl.NumberFormat('fr-CA').format(plan.ai_credits_monthly)}
            </strong>{' '}
            crédits IA par mois
          </FeatureLi>
        )}
        {limitEntries.map(([key, value]) => (
          <FeatureLi key={key}>
            {humanizeKey(key)} : {formatLimitValue(value)}
          </FeatureLi>
        ))}
        {plan.overage_credit_price > 0 && (
          <FeatureLi>
            Crédits additionnels à {formatPrice(plan.overage_credit_price, plan.currency)} l’unité
          </FeatureLi>
        )}
      </ul>

      <div className="mt-auto">
        <ChooseButton highlighted={highlighted} onChoose={onChoose} label={PLANS_SECTION.ctaSubscribe} />
      </div>
    </PlanFrame>
  );
}

function TeaserPlanCard({ plan, onChoose }: { plan: TeaserPlan; onChoose: () => void }) {
  return (
    <PlanFrame highlighted={plan.highlighted}>
      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
      <p className="mt-1 text-sm text-muted">{plan.tagline}</p>

      <div className="mt-5">
        <p className="flex items-baseline gap-1">
          <span className="text-4xl font-extrabold tracking-tight text-foreground">{plan.priceLabel}</span>
        </p>
        <p className="mt-1 text-xs text-muted">{plan.period}</p>
      </div>

      <ul className="mt-6 space-y-3">
        {plan.features.map((f) => (
          <FeatureLi key={f}>{f}</FeatureLi>
        ))}
      </ul>

      <div className="mt-auto">
        <ChooseButton highlighted={plan.highlighted} onChoose={onChoose} label={PLANS_SECTION.ctaLogin} />
      </div>
    </PlanFrame>
  );
}

function PlanSkeleton() {
  return (
    <div className="flex h-full animate-pulse flex-col rounded-token-xl border border-border bg-surface p-6 shadow-elevation-1 sm:p-8">
      <div className="h-5 w-24 rounded-full bg-surface-2" />
      <div className="mt-3 h-3 w-40 rounded-full bg-surface-2" />
      <div className="mt-6 h-10 w-32 rounded-token bg-surface-2" />
      <div className="mt-6 space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-3 w-full rounded-full bg-surface-2" />
        ))}
      </div>
      <div className="mt-8 h-10 w-full rounded-token bg-surface-2" />
    </div>
  );
}
